import api from './api'

// Types
export interface InvoiceLineItem {
  id: string
  invoice_id: string
  clinic_id: string
  practice_product_id: string | null
  appointment_type_id: string | null
  medication_request_id: string | null
  lab_order_id: string | null
  description: string
  quantity: number
  unit_price_cents: number
  amount_cents: number
  cpt_code: string | null
  icd_codes: string[] | null
  sort_order: number
}

export type InvoiceSource =
  | 'manual'
  | 'auto_from_booking'
  | 'auto_from_encounter'
  | 'auto_from_prescription'
  | 'auto_from_lab_order'
  | 'subscription_refill'
  | 'amendment_delta'

export interface InvoicePrescriptionSummary {
  id: string
  status: string
  is_subscription: boolean
  treatment_type: string | null
}

export interface InvoiceLabOrderSummary {
  id: string
  panel_name: string | null
  status: string
}

export interface InvoiceAppointmentSummary {
  id: string
  start_time: string | null
  status: string | null
  appointment_type_id: string | null
  appointmentType?: {
    id: string
    name: string
  } | null
}

export interface InvoicePatient {
  id: string
  first_name: string
  middle_name?: string | null
  last_name: string
  dob?: string | null
  gender?: string | null
  email?: string
  phone?: string
}

export interface InvoiceTransaction {
  id: string
  amount: number
  status: string
  processed_at: string
  card_last4: string | null
  card_brand: string | null
  payment_method?: string | null
  stripe_charge_id?: string | null
}

export interface Invoice {
  id: string
  clinic_id: string
  patient_id: string
  appointment_id: string | null
  encounter_id: string | null
  prescription_id: string | null
  lab_order_id: string | null
  parent_invoice_id: string | null
  invoice_number: string | null
  status: 'draft' | 'open' | 'sent' | 'paid' | 'partially_paid' | 'void' | 'uncollectible'
  issued_at: string | null
  due_at: string | null
  subtotal_cents: number
  tax_cents: number
  discount_cents: number
  total_cents: number
  amount_paid_cents: number
  balance_cents: number
  currency: string
  notes: string | null
  source: InvoiceSource
  stripe_payment_intent_id: string | null
  payment_page_token: string | null
  pdf_url: string | null
  metadata?: Record<string, unknown>
  created_at: string
  updated_at: string
  lineItems?: InvoiceLineItem[]
  patient?: InvoicePatient
  transactions?: InvoiceTransaction[]
  prescription?: InvoicePrescriptionSummary | null
  labOrder?: InvoiceLabOrderSummary | null
  appointment?: InvoiceAppointmentSummary | null
}

export interface InvoiceListResponse {
  invoices: Invoice[]
  total: number
  page: number
  limit: number
}

export interface CreateInvoiceLineItemInput {
  description: string
  quantity?: number
  unitPriceCents: number
  cptCode?: string
  icdCodes?: string[]
  practiceProductId?: string
  appointmentTypeId?: string
  sortOrder?: number
}

export interface CreateInvoiceInput {
  patientId: string
  lineItems?: CreateInvoiceLineItemInput[]
  notes?: string
  dueAt?: string
}

export interface InvoiceListParams {
  patientId?: string
  status?: string
  startDate?: string
  endDate?: string
  source?: string
  page?: number
  limit?: number
}

export interface InvoiceSettings {
  prefix: string
  separator: string
  include_year: boolean
  year_format: 'YYYY' | 'YY'
  starting_number: number
  zero_pad_width: number
  auto_invoice_mode?: 'off' | 'on_booking' | 'on_completion'
}

export interface AgingBucket {
  bucket: string
  count: number
  totalBalanceCents: number
  invoices: Array<{
    id: string
    invoiceNumber: string
    patientName: string
    patientId: string
    totalCents: number
    balanceCents: number
    issuedAt: string
    dueAt: string | null
    status: string
  }>
}

export interface AgingReport {
  buckets: AgingBucket[]
  grandTotalBalance: number
  grandTotalCount: number
}

export interface InvoiceSummaryReport {
  invoicedTotalCents: number
  collectedTotalCents: number
  collectionRate: number
  totalInvoices: number
  paidInvoices: number
  openInvoices: number
  avgDaysToPayment: number | null
  statusBreakdown: Record<string, { count: number; totalCents: number }>
  sourceBreakdown: Record<InvoiceSource, { count: number; totalCents: number; paidCents: number }>
  topProducts: Array<{
    practiceProductId: string | null
    description: string
    totalRevenueCents: number
    totalQuantity: number
  }>
}

export interface PracticeProductsRevenueReport {
  products: Array<{
    practiceProductId: string | null
    description: string
    cptCode: string | null
    totalRevenueCents: number
    totalQuantity: number
    invoiceCount: number
    revenuePercent: number
  }>
  totalRevenueCents: number
}

export const invoicesService = {
  // Invoice CRUD
  async create(clinicId: string, input: CreateInvoiceInput): Promise<Invoice> {
    const response = await api.post(`/emr/${clinicId}/invoices`, input)
    return response.data?.data
  },

  async createFromAppointment(clinicId: string, appointmentId: string): Promise<Invoice> {
    const response = await api.post(`/emr/${clinicId}/invoices/from-appointment/${appointmentId}`)
    return response.data?.data
  },

  async createFromEncounter(clinicId: string, encounterId: string): Promise<Invoice> {
    const response = await api.post(`/emr/${clinicId}/invoices/from-encounter/${encounterId}`)
    return response.data?.data
  },

  async list(clinicId: string, params: InvoiceListParams = {}): Promise<InvoiceListResponse> {
    const queryParams = new URLSearchParams()
    if (params.patientId) queryParams.set('patientId', params.patientId)
    if (params.status) queryParams.set('status', params.status)
    if (params.startDate) queryParams.set('startDate', params.startDate)
    if (params.endDate) queryParams.set('endDate', params.endDate)
    if (params.source) queryParams.set('source', params.source)
    if (params.page) queryParams.set('page', String(params.page))
    if (params.limit) queryParams.set('limit', String(params.limit))

    const response = await api.get(`/emr/${clinicId}/invoices?${queryParams.toString()}`)
    return response.data?.data
  },

  async getById(clinicId: string, invoiceId: string): Promise<Invoice> {
    const response = await api.get(`/emr/${clinicId}/invoices/${invoiceId}`)
    return response.data?.data
  },

  async update(clinicId: string, invoiceId: string, input: { notes?: string; dueAt?: string }): Promise<Invoice> {
    const response = await api.put(`/emr/${clinicId}/invoices/${invoiceId}`, input)
    return response.data?.data
  },

  // Line items
  async addLineItem(clinicId: string, invoiceId: string, input: CreateInvoiceLineItemInput): Promise<InvoiceLineItem> {
    const response = await api.post(`/emr/${clinicId}/invoices/${invoiceId}/line-items`, input)
    return response.data?.data
  },

  async updateLineItem(clinicId: string, invoiceId: string, lineItemId: string, input: Partial<CreateInvoiceLineItemInput>): Promise<InvoiceLineItem> {
    const response = await api.put(`/emr/${clinicId}/invoices/${invoiceId}/line-items/${lineItemId}`, input)
    return response.data?.data
  },

  async removeLineItem(clinicId: string, invoiceId: string, lineItemId: string): Promise<void> {
    await api.delete(`/emr/${clinicId}/invoices/${invoiceId}/line-items/${lineItemId}`)
  },

  // Lifecycle
  async issue(clinicId: string, invoiceId: string): Promise<Invoice> {
    const response = await api.post(`/emr/${clinicId}/invoices/${invoiceId}/issue`)
    return response.data?.data
  },

  async sendPaymentLink(
    clinicId: string,
    invoiceId: string,
    channels: string[] = ['email'],
  ): Promise<{
    paymentUrl: string
    expiresAt: string | null
    sent?: string[]
    errors?: Array<{ channel: string; reason: string }>
  }> {
    const response = await api.post(`/emr/${clinicId}/invoices/${invoiceId}/send-link`, { channels })
    return response.data?.data
  },

  async getPaymentLink(clinicId: string, invoiceId: string): Promise<{ paymentUrl: string; expiresAt: string | null }> {
    const response = await api.get(`/emr/${clinicId}/invoices/${invoiceId}/payment-link`)
    return response.data?.data
  },

  async getPatientPaymentMethods(clinicId: string, patientId: string): Promise<Array<{ id: string; brand: string; last4: string; expMonth?: number; expYear?: number }>> {
    const response = await api.get(`/emr/${clinicId}/patients/${patientId}/payment-methods`)
    return response.data?.data || []
  },

  async charge(clinicId: string, invoiceId: string, paymentMethodId: string): Promise<{ transaction: InvoiceTransaction }> {
    const response = await api.post(`/emr/${clinicId}/invoices/${invoiceId}/charge`, { paymentMethodId })
    return response.data?.data
  },

  async void(clinicId: string, invoiceId: string, reason?: string): Promise<Invoice> {
    const response = await api.post(`/emr/${clinicId}/invoices/${invoiceId}/void`, { reason })
    return response.data?.data
  },

  async getPdf(clinicId: string, invoiceId: string): Promise<{ pdfUrl: string }> {
    const response = await api.get(`/emr/${clinicId}/invoices/${invoiceId}/pdf`)
    return response.data?.data
  },

  // Patient-scoped
  async getPatientInvoices(clinicId: string, patientId: string): Promise<Invoice[]> {
    const response = await api.get(`/emr/${clinicId}/patients/${patientId}/invoices`)
    return response.data?.data
  },

  // Settings
  async getSettings(clinicId: string): Promise<{ settings: InvoiceSettings; nextInvoiceNumberPreview: string }> {
    const response = await api.get(`/emr/${clinicId}/invoice-settings`)
    return response.data?.data
  },

  async updateSettings(clinicId: string, settings: Partial<InvoiceSettings>): Promise<{ settings: InvoiceSettings; nextInvoiceNumberPreview: string }> {
    const response = await api.put(`/emr/${clinicId}/invoice-settings`, settings)
    return response.data?.data
  },

  // Reports
  async getAgingReport(clinicId: string): Promise<AgingReport> {
    const response = await api.get(`/emr/${clinicId}/invoices/reports/aging`)
    return response.data?.data
  },

  async getSummaryReport(clinicId: string, params: { startDate?: string; endDate?: string } = {}): Promise<InvoiceSummaryReport> {
    const queryParams = new URLSearchParams()
    if (params.startDate) queryParams.set('startDate', params.startDate)
    if (params.endDate) queryParams.set('endDate', params.endDate)
    const response = await api.get(`/emr/${clinicId}/invoices/reports/summary?${queryParams.toString()}`)
    return response.data?.data
  },

  async getPracticeProductsRevenue(clinicId: string, params: { startDate?: string; endDate?: string } = {}): Promise<PracticeProductsRevenueReport> {
    const queryParams = new URLSearchParams()
    if (params.startDate) queryParams.set('startDate', params.startDate)
    if (params.endDate) queryParams.set('endDate', params.endDate)
    const response = await api.get(`/emr/${clinicId}/practice-products/reports/revenue?${queryParams.toString()}`)
    return response.data?.data
  },
}
